import { useEffect, useState } from "react";
import { Shield, ShieldCheck } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  PageBody,
  Spinner,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";

interface PlatformSettings {
  twoFactorEnabled: boolean;
}

export function TwoFactorSettings() {
  const toast = useToast();
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const data = await api.get<PlatformSettings>("/platform/settings");
      setSettings(data);
    } catch {
      toast.error("Could not load settings.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function toggle() {
    if (!settings) return;
    setSaving(true);
    try {
      const updated = await api.patch<PlatformSettings>("/platform/settings", {
        twoFactorEnabled: !settings.twoFactorEnabled,
      });
      setSettings(updated);
      toast.success(
        updated.twoFactorEnabled
          ? "Two-factor authentication enabled."
          : "Two-factor authentication disabled.",
      );
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.message);
      } else {
        toast.error("Could not save settings.");
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <PageBody>
        <div className="grid min-h-[40vh] place-items-center">
          <Spinner className="size-6 text-brand" />
        </div>
      </PageBody>
    );
  }

  const enabled = settings?.twoFactorEnabled ?? false;

  return (
    <PageBody>
      <div className="mx-auto flex max-w-2xl flex-col gap-5">
        <Card>
          <CardHeader
            title="Two-factor authentication"
            description="Super admin accounts must enter a 6-digit code sent to their email after signing in."
          />
          <CardBody className="flex flex-col gap-4">
            <div className="flex items-center justify-between rounded-lg border border-rule bg-surface p-4">
              <div className="flex flex-col gap-0.5">
                <p className="font-medium text-ink">Require 2FA for super admins</p>
                <p className="text-sm text-muted">
                  {enabled ? "Currently enabled" : "Currently disabled"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {enabled ? (
                  <Badge tone="success">Enabled</Badge>
                ) : (
                  <Badge tone="neutral">Disabled</Badge>
                )}
                <Button
                  loading={saving}
                  onClick={() => void toggle()}
                  variant={enabled ? "danger" : "primary"}
                  size="sm"
                >
                  {enabled ? "Disable" : "Enable"}
                </Button>
              </div>
            </div>

            {enabled && (
              <Alert kind="info" title="2FA is active">
                Super admins will be prompted for a verification code after
                entering their password.
              </Alert>
            )}

            {!enabled && (
              <Alert kind="warning" title="2FA is off">
                Super admins sign in with just their password. Enable 2FA to
                require a second factor.
              </Alert>
            )}
          </CardBody>
        </Card>
      </div>
    </PageBody>
  );
}
